import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../context/ToastContext';
import PageTableLayout from '../../../../components/PageTableLayout';
import apiService from '../../../../services/apiService';
import { FaFilter, FaTimes, FaEye, FaPencilAlt } from 'react-icons/fa';
import { Button } from '../../../../components/ui';

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <span className="text-gray-700">Education Standards</span>
  </span>
);

const EduStandard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    name: '',
    uri: '',
    status: ''
  });
  const [limit, setLimit] = useState(10);
  const [isFiltered, setIsFiltered] = useState(false);

  const fetchData = useCallback(async (page, filterParams = {}, itemsPerPage = limit) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page,
        limit: itemsPerPage,
        totalCount: true,
        select: 'name,uri,order,status'
      });
      
      // Add any filter parameters
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await apiService.request(`/i/ims/edustandard?${params.toString()}`);
      
      // Ensure data is always an array
      const responseData = Array.isArray(response) ? response : (response.data || []);
      setData(responseData);
      setTotalRecords(response.totalCount || responseData.length);
      setCurrentPage(page);
      
      // Check if any filters are applied
      setIsFiltered(Object.values(filterParams).some(value => value !== ''));
    } catch (error) {
      console.error('Error fetching education standards:', error);
      showToast('error', 'Failed to fetch education standards');
    } finally {
      setLoading(false);
    }
  }, [limit, showToast]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterSubmit = () => {
    fetchData(1, filters);
  };

  const handleFilterReset = () => {
    const resetFilters = {
      name: '',
      uri: '',
      status: ''
    };
    setFilters(resetFilters);
    fetchData(1, resetFilters);
  };

  const handlePageChange = (page) => {
    fetchData(page, filters);
  };

  const handleLimitChange = (value) => {
    const newLimit = Number(typeof value === 'object' ? value.target.value : value);
    setLimit(newLimit);
    fetchData(1, filters, newLimit);
  };

  const columns = [
    { 
      key: 'name', 
      title: 'Name',
      filterComponent: (
        <input 
          className="w-full border px-2 py-1 rounded text-xs" 
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => handleFilterChange('name', e.target.value)}
        />
      )
    },
    { 
      key: 'uri', 
      title: 'URI',
      filterComponent: (
        <input 
          className="w-full border px-2 py-1 rounded text-xs" 
          placeholder="Filter by URI"
          value={filters.uri}
          onChange={(e) => handleFilterChange('uri', e.target.value)}
        />
      )
    },
    { 
      key: 'order', 
      title: 'Order',
      filterComponent: null // No filter for order column
    },
    { 
      key: 'status', 
      title: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.status ? 'Active' : 'Inactive'}
        </span>
      ),
      filterComponent: (
        <select 
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/edu-standards/view/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="View"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            <FaEye className="h-4 w-4" />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/edu-standards/edit/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="Edit"
            className="text-amber-600 border-amber-600 hover:bg-amber-50"
          >
            <FaPencilAlt className="h-4 w-4" />
          </Button>
        </div>
      ),
      filterComponent: (
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleFilterSubmit}
            disabled={!filters.name && !filters.uri && !(filters.status === 'true' || filters.status === 'false')}
          >
            <FaFilter className="h-3 w-3 mr-1" />
            Filter
          </Button>
          <Button
            variant="outline"
            size="xs"
            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleFilterReset}
            disabled={!isFiltered}
          >
            <FaTimes className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      )
    }
  ];

  const handleView = (row) => {
    navigate(`/course-programs/edu-standards/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/course-programs/edu-standards/edit/${row.id}`);
  };

  const handleAddNew = () => {
    navigate('/course-programs/edu-standards/add');
  };

  const actionButtons = [
    {
      label: 'Add New',
      onClick: handleAddNew,
      className: 'bg-blue-600 hover:bg-blue-700'
    }
  ];

  return (

    <PageTableLayout
      title="Education Standards"
      breadcrumb={breadcrumb}
      columns={columns}
      data={data || []}
      actions={actionButtons}
      loading={loading}
      pagination={{
        currentPage,
        totalRecords,
        totalPages: Math.ceil(totalRecords / limit),
        limit,
        onLimitChange: handleLimitChange
      }}
      onPageChange={handlePageChange}
      showFiltersInTable={true}
    />
  );
};

export default EduStandard;